import React, { useState } from 'react';
import styled from 'styled-components';
import StatsBox from './StatsBox';
import VitalSignModal from './VitalSignModal';
import { Flex } from './shared/Flex';
import { isMobile } from '@biosensesignal/web-sdk';
import { theme } from '../style/theme';
import media from '../style/media';
import { vitalSignDescriptions } from '../data/vitalSignDescriptions';

const Wrapper = styled(Flex)`
  display: flex;
  position: relative;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 80px;
  width: 100%;
  max-width: 90%;
  overflow: hidden;
  background: ${theme.colors.neutral.white};
  backdrop-filter: blur(20px);
  border: 1px solid ${theme.colors.neutral.gray200};
  border-radius: ${theme.borderRadius['2xl']};
  padding: ${theme.spacing[4]} ${theme.spacing[6]};
  box-sizing: border-box;
  box-shadow: ${theme.shadows.lg};
  transition: all ${theme.transitions.normal};
  margin: 0 auto;
  
  ${media.tablet`
    max-width: 600px;
    padding: ${theme.spacing[5]} ${theme.spacing[8]};
  `}
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows['2xl']};
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: ${theme.colors.primary.gradient};
    border-radius: ${theme.borderRadius['2xl']} ${theme.borderRadius['2xl']} 0 0;
  }
`;

const BoxesWrapper = styled(Flex)`
  gap: ${theme.spacing[4]};
  flex-wrap: wrap;
  justify-content: center;
  width: 100%;
  
  ${media.tablet`
    gap: ${theme.spacing[6]};
  `}
`;

// Instructional text for clickable items
const InstructionalText = styled.div`
  text-align: center;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.neutral.gray500};
  margin-bottom: ${theme.spacing[4]};
  font-weight: ${theme.typography.fontWeight.medium};
  
  /* Make it more prominent on mobile */
  @media (hover: none) and (pointer: coarse) {
    font-size: ${theme.typography.fontSize.base};
    color: ${theme.colors.neutral.gray600};
    margin-bottom: ${theme.spacing[5]};
  }
`;

interface VitalSign<T> {
  value: T;
  isEnabled: boolean;
}

interface IStats {
  /**
   *  Object - contains health stats info
   */
  vitalSigns: {
    // Basic Vitals
    pulseRate: VitalSign<number>;
    respirationRate: VitalSign<number>;
    bloodPressure: VitalSign<BloodPressureValue>;
    oxygenSaturation: VitalSign<number>;
    spo2: VitalSign<number>;
    
    // Stress & Wellness
    stress: VitalSign<number>;
    stressIndex: VitalSign<number>;
    normalizedStressIndex: VitalSign<number>;
    wellnessLevel: VitalSign<number>;
    wellnessIndex: VitalSign<number>;
    heartAge: VitalSign<number>;
    
    // HRV Analysis
    hrvSdnn: VitalSign<number>;
    rmssd: VitalSign<number>;
    sd1: VitalSign<number>;
    sd2: VitalSign<number>;
    lfhf: VitalSign<number>;
    rri: VitalSign<number>;
    meanRri: VitalSign<number>;
    
    // Autonomic Nervous System
    pnsIndex: VitalSign<number>;
    pnsZone: VitalSign<number>;
    snsIndex: VitalSign<number>;
    snsZone: VitalSign<number>;
    
    // Risk Assessments
    highBloodPressureRisk: VitalSign<number>;
    ascvdRisk: VitalSign<number>;
    
    // Quality Metrics
    prq: VitalSign<number>;
  };
  isMeasuring?: boolean;
}

export type BloodPressureValue = {
  systolic: number;
  diastolic: number;
};

// Define vital sign categories with their reference ranges
const vitalSignCategories = {
  basic: {
    title: "Basic Vitals",
    vitals: [
      { key: 'pulseRate', unit: 'bpm', range: { min: 60, max: 100 } },
      { key: 'respirationRate', unit: '/min', range: { min: 12, max: 20 } },
      { key: 'bloodPressure', unit: 'mmHg', range: { min: 90, max: 140 } },
      { key: 'oxygenSaturation', unit: '%', range: { min: 95, max: 100 } },
    ]
  },
  wellness: {
    title: "Wellness & Quality",
    vitals: [
      { key: 'prq', unit: '%', range: { min: 70, max: 100 } },
      { key: 'wellnessLevel', unit: '', range: { min: 0, max: 100 } },
      { key: 'wellnessIndex', unit: '', range: { min: 0, max: 100 } },
      { key: 'heartAge', unit: 'yrs', range: { min: 20, max: 80 } },
      { key: 'normalizedStressIndex', unit: '', range: { min: 0, max: 100 } },
    ]
  },
  stress: {
    title: "Stress Analysis",
    vitals: [
      { key: 'stress', unit: '', range: { min: 0, max: 100 } },
      { key: 'stressIndex', unit: '', range: { min: 0, max: 100 } },
    ]
  },
  hrv: {
    title: "Heart Rate Variability",
    vitals: [
      { key: 'hrvSdnn', unit: 'ms', range: { min: 20, max: 50 } },
      { key: 'rmssd', unit: 'ms', range: { min: 20, max: 50 } },
      { key: 'sd1', unit: 'ms', range: { min: 10, max: 40 } },
      { key: 'sd2', unit: 'ms', range: { min: 20, max: 60 } },
      { key: 'lfhf', unit: '', range: { min: 0.5, max: 2.0 } },
      { key: 'rri', unit: 'ms', range: { min: 600, max: 1000 } },
      { key: 'meanRri', unit: 'ms', range: { min: 600, max: 1000 } },
    ]
  },
  autonomic: {
    title: "Autonomic Nervous System",
    vitals: [
      { key: 'pnsIndex', unit: '', range: { min: 0, max: 100 } },
      { key: 'pnsZone', unit: '', range: { min: 1, max: 5 } },
      { key: 'snsIndex', unit: '', range: { min: 0, max: 100 } },
      { key: 'snsZone', unit: '', range: { min: 1, max: 5 } },
    ]
  },
  risks: {
    title: "Risk Assessments",
    vitals: [
      { key: 'highBloodPressureRisk', unit: '%', range: { min: 0, max: 100 } },
      { key: 'ascvdRisk', unit: '%', range: { min: 0, max: 100 } },
    ]
  }
};

const Stats = ({ vitalSigns, isMeasuring = false }: IStats) => {
  const [selectedVitalSign, setSelectedVitalSign] = useState<string | null>(null);
  const [selectedVitalSignData, setSelectedVitalSignData] = useState<{
    value: string | number;
    unit?: string;
  } | null>(null);
  
  const bloodPressureToDisplay =
    vitalSigns.bloodPressure.value?.systolic &&
    vitalSigns.bloodPressure.value?.diastolic
      ? vitalSigns.bloodPressure.value.systolic +
        '/' +
        vitalSigns.bloodPressure.value.diastolic
      : '--';

  const getVitalSignValue = (key: string): string | number => {
    if (key === 'bloodPressure') {
      return bloodPressureToDisplay;
    }
    const vitalSign = vitalSigns[key as keyof typeof vitalSigns];
    if (!vitalSign) return '--';
    
    if (!vitalSign.isEnabled) return 'N/A';
    if (vitalSign.value === null || vitalSign.value === undefined) return '--';
    
    // For non-bloodPressure vital signs, the value should be string or number
    if (typeof vitalSign.value === 'object') return '--';
    
    return vitalSign.value;
  };

  const getVitalSignTitle = (key: string, compact: boolean = true) => {
    const titles: Record<string, { full: string; compact: string }> = {
      // Basic Vitals
      pulseRate: { full: 'Pulse Rate', compact: 'PR' },
      respirationRate: { full: 'Respiration Rate', compact: 'RR' },
      bloodPressure: { full: 'Blood Pressure', compact: 'BP' },
      oxygenSaturation: { full: 'Oxygen Saturation', compact: 'SpO2' },
      spo2: { full: 'SpO2', compact: 'SpO2' },
      
      // Wellness & Quality
      prq: { full: 'Pulse Rate Quality', compact: 'PRQ' },
      wellnessLevel: { full: 'Wellness Level', compact: 'WL' },
      wellnessIndex: { full: 'Wellness Index', compact: 'WI' },
      heartAge: { full: 'Heart Age', compact: 'HA' },
      normalizedStressIndex: { full: 'Normalized Stress Index', compact: 'NSI' },
      
      // Stress
      stress: { full: 'Stress Level', compact: 'SL' },
      stressIndex: { full: 'Stress Index', compact: 'SI' },
      
      // HRV
      hrvSdnn: { full: 'HRV SDNN', compact: 'SDNN' },
      rmssd: { full: 'RMSSD', compact: 'RMSSD' },
      sd1: { full: 'SD1', compact: 'SD1' },
      sd2: { full: 'SD2', compact: 'SD2' },
      lfhf: { full: 'LF/HF Ratio', compact: 'LF/HF' },
      rri: { full: 'R-R Intervals', compact: 'RRI' },
      meanRri: { full: 'Mean RRI', compact: 'MRRI' },
      
      // Autonomic
      pnsIndex: { full: 'PNS Index', compact: 'PNSI' },
      pnsZone: { full: 'PNS Zone', compact: 'PNSZ' },
      snsIndex: { full: 'SNS Index', compact: 'SNSI' },
      snsZone: { full: 'SNS Zone', compact: 'SNSZ' },
      
      // Risk Assessments
      highBloodPressureRisk: { full: 'High BP Risk', compact: 'BPR' },
      ascvdRisk: { full: 'ASCVD Risk', compact: 'CVD' },
    };
    return compact ? titles[key]?.compact || key : titles[key]?.full || key;
  };

  // Check if vital sign has description data
  const hasDescription = (key: string): boolean => {
    return key in vitalSignDescriptions;
  };

  // Handle vital sign click
  const handleVitalSignClick = (key: string, unit?: string) => {
    // Don't allow clicking during measurement
    if (isMeasuring) {
      return;
    }
    
    if (hasDescription(key)) {
      setSelectedVitalSign(key);
      setSelectedVitalSignData({
        value: getVitalSignValue(key),
        unit: unit
      });
    }
  };

  // Close modal
  const handleCloseModal = () => {
    setSelectedVitalSign(null);
    setSelectedVitalSignData(null);
  };

  // Simple layout for now - show key vitals
  if (isMeasuring) {
    // Show only priority vitals during measurement that have descriptions
    const priorityVitals = ['pulseRate', 'respirationRate', 'stress', 'hrvSdnn', 'prq', 'wellnessLevel'];
    const priorityVitalsWithDescriptions = priorityVitals.filter(key => hasDescription(key));
    
    return (
      <>
        <Wrapper>
          {/* No instruction text during measurement */}
          <BoxesWrapper>
            {priorityVitalsWithDescriptions.map(key => (
              <StatsBox
                key={key}
                title={getVitalSignTitle(key, true)}
                value={getVitalSignValue(key)}
                clickable={false}
                onClick={() => handleVitalSignClick(key)}
              />
            ))}
          </BoxesWrapper>
        </Wrapper>
        
        <VitalSignModal
          isOpen={selectedVitalSign !== null}
          onClose={handleCloseModal}
          vitalSignKey={selectedVitalSign}
          currentValue={selectedVitalSignData?.value}
          unit={selectedVitalSignData?.unit}
        />
      </>
    );
  }

  // Full categorized view when not measuring
  return (
    <>
      <Wrapper>
        <InstructionalText>
          Tap any measurement for details
        </InstructionalText>
        
        {Object.entries(vitalSignCategories).map(([categoryKey, category]) => {
          // Filter vitals to only show those with descriptions
          const vitalsWithDescriptions = category.vitals.filter(({ key }) => hasDescription(key));
          
          // Only show category if it has vitals with descriptions
          if (vitalsWithDescriptions.length === 0) {
            return null;
          }
          
          return (
            <div key={categoryKey} style={{ marginBottom: theme.spacing[4] }}>
              <h4 style={{ 
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.semibold,
                color: theme.colors.primary.main,
                margin: `0 0 ${theme.spacing[2]} 0`,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {category.title}
              </h4>
              <BoxesWrapper>
                {vitalsWithDescriptions.map(({ key, unit, range }) => (
                  <StatsBox
                    key={key}
                    title={getVitalSignTitle(key, true)}
                    value={getVitalSignValue(key)}
                    unit={unit}
                    clickable={true}
                    onClick={() => handleVitalSignClick(key, unit)}
                  />
                ))}
              </BoxesWrapper>
            </div>
          );
        })}
      </Wrapper>
      
      <VitalSignModal
        isOpen={selectedVitalSign !== null}
        onClose={handleCloseModal}
        vitalSignKey={selectedVitalSign}
        currentValue={selectedVitalSignData?.value}
        unit={selectedVitalSignData?.unit}
      />
    </>
  );
};

export default Stats;
